import { apiClient } from '@/api/api-client';
import {
  GEOLOCATION_CONSENT_VERSION,
  collectorPlacesResponseSchema,
  collectionPlaceQueryKey,
  consentResponseSchema,
  declaredPlaceHistoryResponseSchema,
  placesResponseSchema,
  readCollectorPlaces,
  reverseResponseSchema,
  type Place,
} from './collection-place.contract';

type Language = 'fr' | 'en';
type Position = { lat: number; lon: number };
const PLACE_STALE_TIME_MS = 10 * 60_000;

export const collectionPlaceQueryOptions = (userId: string, language: Language) => ({
  queryKey: collectionPlaceQueryKey(userId, language),
  queryFn: async () => collectionPlaceService.reversePlace(
    await collectionPlaceService.browserPosition(),
    language,
  ),
  staleTime: PLACE_STALE_TIME_MS,
  gcTime: PLACE_STALE_TIME_MS,
});

const POSITION_TIMEOUT_MS = 10_000;

export const collectionPlaceService = {
  async searchPlaces(query: string, lang: Language, countryCode?: string): Promise<Place[]> {
    const { data } = await apiClient.get<unknown>('/geo/places', {
      params: { q: query.trim(), lang, countryCode },
    });
    return placesResponseSchema.parse(data).items;
  },

  async reversePlace(position: Position, lang: Language): Promise<Place> {
    const { data } = await apiClient.post<unknown>('/geo/reverse', { ...position, lang });
    return reverseResponseSchema.parse(data).place;
  },

  async collectorPlaces(userId: string | number) {
    const { data } = await apiClient.get<unknown>(`/stats/collectors/${encodeURIComponent(String(userId))}/places`);
    const items = collectorPlacesResponseSchema.parse(data);
    return readCollectorPlaces(items);
  },

  async recentDeclaredPlaces() {
    const { data } = await apiClient.get<unknown>('/stats/me/declared-places');
    return declaredPlaceHistoryResponseSchema.parse(data).data.items;
  },

  async recordConsent(decision: 'granted' | 'denied') {
    const { data } = await apiClient.put<unknown>('/users/me/geolocation-consent', {
      decision,
      version: GEOLOCATION_CONSENT_VERSION,
    });
    return consentResponseSchema.parse(data);
  },

  /** The browser position, coarse on purpose: a city is all that is needed. */
  browserPosition(): Promise<Position> {
    return new Promise((resolve, reject) => {
      if (!('geolocation' in navigator)) {
        reject(new Error('GEOLOCATION_UNSUPPORTED'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        ({ coords }) => resolve({ lat: coords.latitude, lon: coords.longitude }),
        (error) => reject(new Error(`GEOLOCATION_${error.code}`)),
        { enableHighAccuracy: false, timeout: POSITION_TIMEOUT_MS, maximumAge: 10 * 60_000 },
      );
    });
  },
};
